import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { user, refresh } = useAuth();
  const inWishlist = user?.wishlist?.includes(product.id);

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast("Sign in to save favourites"); return; }
    try {
      if (inWishlist) await api.delete(`/wishlist/${product.id}`);
      else await api.post(`/wishlist/${product.id}`);
      await refresh();
      toast.success(inWishlist ? "Removed from wishlist" : "Saved to wishlist");
    } catch { toast.error("Please try again"); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 6) * 0.05, ease: [0.2, 0.7, 0.2, 1] }}
      className="group"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative img-hover bg-ivory aspect-[3/4]">
          {product.images?.[0] && (
            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
          )}
          {product.compare_at_price && product.compare_at_price > product.price && (
            <div className="absolute top-3 left-3 overline text-[10px] bg-cream/90 px-2 py-1">Save {Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)}%</div>
          )}
          {product.is_new_arrival && (
            <div className="absolute top-3 right-3 overline text-[10px] bg-emerald text-ivory px-2 py-1">New</div>
          )}
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-cream/70 flex items-center justify-center overline text-emerald">Sold Out</div>
          )}
          <button
            data-testid={`wishlist-${product.slug}`}
            onClick={toggleWishlist}
            className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-cream/95 flex items-center justify-center hover:bg-emerald hover:text-ivory transition-colors"
            aria-label="Add to wishlist"
          >
            <Heart className={`w-4 h-4 ${inWishlist ? "fill-emerald text-emerald" : ""}`} />
          </button>
        </div>
        <div className="pt-4 flex items-start justify-between gap-4">
          <div>
            <div className="font-serif text-[17px] tracking-tight leading-tight">{product.name}</div>
            {product.short_description && (
              <div className="text-xs text-charcoal/55 mt-1">{product.short_description}</div>
            )}
          </div>
          <div className="text-right">
            <div className="font-serif text-[17px] tracking-tight">{currency(product.price)}</div>
            {product.compare_at_price && product.compare_at_price > product.price && (
              <div className="text-[11px] text-charcoal/40 line-through">{currency(product.compare_at_price)}</div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
